import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import { Box, Breadcrumbs, Button, Grid, Link, TextField, Typography } from '@mui/material';

import { UMAReduxState } from '../../../store/rootReducer';
import { Application, createApplicationFormSchema, CreateApplicationFormValues } from '../../../store/Applications/ApplicationModels';
import { createApplication } from '../../../store/Applications/ApplicationActions';
import Can from '../../Can/Can';
import RolesList from '../../FilterList/FilterList';
import LeavePageModal from '../../LeavePageModal/LeavePageModal';
import useStyles from './styles';
import policyConstants from '../../../constants/policyConstants';

const FORM_ID = 'create-application-form';

type StateToProps = {
  isCreating: boolean;
  organizationCode: string;
  organizationName: string;
};

type DispatchToProps = {
  createApplication: (organizationCode: string, application: Partial<Application>) => void
};

type GroupCreatePageProps = StateToProps & DispatchToProps;

function GroupCreatePage({
  createApplication,
  isCreating,
  organizationCode,
  organizationName,
}: GroupCreatePageProps) {
  const classes = useStyles();
  const history = useHistory();
  const { control, formState: { isDirty, isValid }, handleSubmit } = useForm<CreateApplicationFormValues>({
    mode: 'onChange',
    resolver: yupResolver(createApplicationFormSchema)
  });

  const [leavePageModalOpen, setLeavePageModalOpen] = useState<boolean>(false);

  const handleBreadcrumbClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    history.push('/applications');
  };

  const onSubmit = (values: CreateApplicationFormValues) => {
    if (!isDirty) return;

    const createPayload: Partial<Application> = {
      code: values.code,
      name: values.name,
      oktaGroupId: values.oktaGroupId || null
    };
    createApplication(organizationCode, createPayload);
  };

  const handleLeavePageModalOpened = () => setLeavePageModalOpen(true);
  const handleLeavePageModalClosed = () => setLeavePageModalOpen(false);
  const handleLeavePageClick = () => history.push('/applications');

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
        <Grid container justifyContent="space-between">
          <Grid item>
            <Breadcrumbs className={classes.breadcrumbs} separator="›" aria-label="breadcrumb">
              <Link
                className={classes.baseBreadcrumb}
                color="inherit"
                href="/applications"
                onClick={handleBreadcrumbClick}
                variant="body2"
              >
                Applications
              </Link>
              <Typography variant="body2">Create Application</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid item>
            <Button color="primary" size="large" variant="outlined" onClick={handleLeavePageModalOpened}>
              Cancel
            </Button>
            <Button
              disabled={isCreating || !isDirty || !isValid}
              color="primary"
              size="large"
              variant="contained"
              type="submit"
              form={FORM_ID}
              sx={{ ml: 1 }}
            >
              {isCreating ? 'Saving' : 'Save'}
            </Button>
          </Grid>
        </Grid>
        <Box mt={2} mb={2}>
          <Grid container item xs={6} spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    autoFocus
                    placeholder="Enter application name"
                    required
                    inputProps={{
                      className: classes.input
                    }}
                    InputProps={{
                      classes: {
                        root: clsx(classes.inputField, classes.nameField)
                      }
                    }}
                    fullWidth
                    error={Boolean(error)}
                    helperText={error?.message ?? ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={8}>
              <Typography variant="h6">Code</Typography>
              <Controller
                name="code"
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    autoFocus
                    placeholder="Enter application code"
                    required
                    inputProps={{
                      className: classes.input
                    }}
                    InputProps={{
                      classes: {
                        root: clsx(classes.inputField, classes.codeField)
                      }
                    }}
                    fullWidth
                    error={Boolean(error)}
                    helperText={error?.message ?? ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={8}>
              <Typography variant="h6">Okta Group Id</Typography>
              <Controller
                name="oktaGroupId"
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    autoFocus
                    placeholder="Enter an Okta Group Id"
                    inputProps={{
                      className: classes.input
                    }}
                    InputProps={{
                      classes: {
                        root: clsx(classes.inputField, classes.codeField)
                      }
                    }}
                    fullWidth
                    error={Boolean(error)}
                    helperText={error?.message ?? ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={8}>
              <Typography variant="h6">Organization</Typography>
              <Typography>{`${organizationCode} / ${organizationName}`}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Box marginTop={1} className={classes.policyContainer}>
                <Typography variant="body1" className={classes.subtitle}>Policy Model</Typography>
                <Typography variant="body1" className={classes.policyModel}>
                  The policy model will be auto-generated once the application is created.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Can requiredPolicy={policyConstants.applications.getRolesAny()}>
          <Grid container spacing={3}>
            <Grid xs={12} md={6} item>
              <RolesList
                onCreateResourcePage={true}
                parentResourceName="applications"
                addItemProps={{
                  handleAddClick: () => { }
                }}
                removeItemProps={{
                  handleDeleteClick: () => { }
                }}
                isLoading={false}
                listResourceName="roles"
                listResources={[]}
              />
            </Grid>
          </Grid>
        </Can>
      </form>

      <LeavePageModal
        open={leavePageModalOpen}
        onClose={handleLeavePageModalClosed}
        onLeave={handleLeavePageClick}
      />
    </>
  );
}

const mapStateToProps = (state: UMAReduxState): StateToProps => ({
  isCreating: state.applications.isCreating,
  organizationCode: state.context.organizationCode,
  organizationName: state.organizations.byId[state.context.organizationCode]?.name ?? ''
});

const mapDispatchToProps: DispatchToProps = {
  createApplication
};

export default connect(mapStateToProps, mapDispatchToProps)(GroupCreatePage);
